import {
  IDS,
  adminEmployee,
  contractTemplate,
  fuelTemplate,
  operationalRequestTemplate,
  partnerOrganization,
  poaDraft,
  templates,
} from './fixtures';
import {
  acceptStaffInvite,
  addFuelItem,
  updateFuelItemStatus,
  addOrgDocument,
  addPaymentOption,
  addPoa,
  confirmOrgInvite,
  createContract,
  createStaffInvite,
  StaffInviteInput,
  documentStatusesList,
  finishOrgInvite,
  getDocumentsByIds,
  getState,
  listDocuments,
  listOrgInvites,
  listStaffInvites,
  rejectOrgInvite,
  removeOrgDocument,
  signDocument,
  startOrgInvite,
  updateOrganization,
} from './state';

function has(query: string, ...needles: string[]) {
  return needles.every((n) => query.includes(n));
}

function asIds(variables: Record<string, unknown>) {
  const raw = variables.documentIds ?? variables.ids ?? variables.id;
  if (Array.isArray(raw)) return raw.map(String);
  if (raw) return [String(raw)];
  return [];
}

function inputOf(variables: Record<string, unknown>) {
  return (variables.input ?? variables) as Record<string, unknown>;
}

function poaUsersResponse() {
  const user = {
    id: IDS.STAFF_ID,
    name: 'Алексей',
    surname: 'Сидоров',
    parentalName: 'Иванович',
    inn: '123456789013',
    organizationName: getState().organization.name,
  };

  return { corePOAUsers: { search: [user], get: [user], checkCertificates: [] } };
}


/**
 * `prepare` вызывают с атрибутами найденной в поиске организации — возвращаем ту,
 * чей ИНН совпал, чтобы дальше приглашение уходило нужному контрагенту.
 */
function prepareTargetOrganization(variables: Record<string, unknown>) {
  const attributes = (variables.organizationAttributes ?? []) as { name: string; value: string }[];
  const inn = attributes.find((attribute) => attribute?.name === 'inn')?.value;
  const known = [getState().organization, partnerOrganization];

  return (
    known.find(
      (organization) =>
        organization.attributes?.find((attribute) => attribute.name === 'inn')?.value === inn,
    ) ?? partnerOrganization
  );
}

export function handleGraphql(query: string, variables: Record<string, unknown> = {}): unknown {
  const q = query.replace(/\s+/g, ' ');
  const state = getState();

  if (has(q, 'coreEmployees', 'me {')) {
    return { coreEmployees: { me: adminEmployee } };
  }

  if (has(q, 'coreOrganizations', 'my {')) {
    const persona = (() => {
      try {
        return localStorage.getItem('mock-persona') || '';
      } catch {
        return '';
      }
    })();
    const loginHint = String(variables.login || '');
    if (persona === 'limited' || loginHint.includes('limited')) {
      return { coreOrganizations: { my: [] } };
    }
    return { coreOrganizations: { my: [state.organization] } };
  }

  if (has(q, 'coreOrganizationParticipations')) {
    return {
      coreOrganizationParticipations: {
        my: [
          {
            id: 'participation-assets',
            role: 'operator',
            application: {
              alias: 'assets',
              widgets: [],
            },
          },
          {
            id: 'participation-1',
            application: {
              alias: 'widget-demo',
              widgets: [
                {
                  id: 'widget-1',
                  header: 'Заявки',
                  icon: '',
                  fields: [
                    {
                      id: 'f1',
                      viewName: 'Список',
                      viewIndex: 1,
                      access: ['read'],
                      path: 'requests',
                      api: 'https://mock.local/widget/requests',
                    },
                  ],
                },
              ],
            },
          },
        ],
      },
    };
  }

  if (has(q, 'coreWidgets')) {
    return {
      coreWidgets: {
        my: [
          {
            id: 'widget-1',
            iconHash: '',
            header: 'Заявки',
            fields: [
              {
                id: 'f1',
                viewName: 'Список',
                viewIndex: 1,
                access: ['read'],
                path: 'requests',
                api: 'https://mock.local/widget/requests',
              },
            ],
          },
        ],
      },
    };
  }

  if (has(q, 'coreEmployees', 'query(filter') && has(q, 'employees')) {
    return { coreEmployees: { query: { employees: state.employees } } };
  }

  if (has(q, 'coreEmployees', 'get')) {
    return { coreEmployees: { get: [adminEmployee] } };
  }

  if (has(q, 'coreEmployeeInvitations') && q.includes('mutation')) {
    if (has(q, 'invite(')) {
      return {
        coreEmployeeInvitations: {
          invite: createStaffInvite(
            variables.participantData as StaffInviteInput | undefined,
            variables.role as string | undefined,
          ),
        },
      };
    }
    if (has(q, 'acceptInvitation')) {
      return {
        coreEmployeeInvitations: {
          acceptInvitation: acceptStaffInvite(
            variables.invitationId ? String(variables.invitationId) : undefined,
            !q.includes('isAccepted: false'),
          ),
        },
      };
    }
  }
  if (has(q, 'coreEmployeeInvitations') && has(q, 'count(')) {
    return { coreEmployeeInvitations: { count: listStaffInvites().length } };
  }
  if (has(q, 'coreEmployeeInvitations', 'isIncoming: true')) {
    return { coreEmployeeInvitations: { query: { invites: listStaffInvites(true) } } };
  }
  if (has(q, 'coreEmployeeInvitations', 'isIncoming: false')) {
    return { coreEmployeeInvitations: { query: { invites: listStaffInvites(false) } } };
  }

  if (has(q, 'coreEmployeePowers')) {
    return {
      coreEmployeePowers: {
        getPowersList: [{ code: 'sign', name: 'Подпись', basePOAID: '', retrustable: false }],
      },
    };
  }

  if (has(q, 'coreDocuments') && q.includes('mutation') && has(q, 'sign')) {
    const documentId = String(variables.documentId ?? inputOf(variables).documentId ?? '');
    signDocument(documentId, variables.signature as string | undefined);
    return { coreDocuments: { sign: { result: true } } };
  }

  if (has(q, 'getStatuses')) {
    return { coreDocuments: { getStatuses: documentStatusesList() } };
  }

  if (has(q, 'coreDocuments', 'query(')) {
    return { coreDocuments: { query: { documents: listDocuments() } } };
  }

  if (has(q, 'coreDocuments', 'get(') || has(q, 'GetCoreDocuments')) {
    return { coreDocuments: { get: getDocumentsByIds(asIds(variables)) } };
  }

  if (has(q, 'coreDocumentTemplates')) {
    return { coreDocumentTemplates: { getPublic: { templates } } };
  }

  if (has(q, 'coreOrganizationPaymentOptions') && q.includes('mutation')) {
    const created = addPaymentOption(inputOf(variables));
    return { coreOrganizationPaymentOptions: { set: created, update: created, create: created } };
  }
  if (has(q, 'coreOrganizationPaymentOptions')) {
    return { coreOrganizationPaymentOptions: { my: state.paymentOptions } };
  }

  if (has(q, 'coreOrganizationDocuments') && q.includes('mutation')) {
    if (has(q, 'delete')) {
      return { coreOrganizationDocuments: { delete: removeOrgDocument(String(variables.id ?? '')) } };
    }
    return { coreOrganizationDocuments: { create: addOrgDocument() } };
  }
  if (has(q, 'coreOrganizationDocuments')) {
    const docs = state.orgDocuments;
    return {
      coreOrganizationDocuments: {
        myDocuments: { documents: docs },
        contractorDocuments: { documents: docs },
      },
    };
  }

  if (has(q, 'coreOrganizationInvitations') && q.includes('mutation')) {
    if (has(q, 'startInvite')) {
      return {
        coreOrganizationInvitations: {
          startInvite: startOrgInvite({
            partnerOrganizationId: String(variables.partnerOrganizationId ?? ''),
            joinDocument: (variables.joinDocument as {
              number?: string;
              date?: string;
              location?: string;
              durationYears?: number;
            } | null) ?? null,
          }),
        },
      };
    }
    if (has(q, 'finishInvite')) {
      return {
        coreOrganizationInvitations: {
          finishInvite: finishOrgInvite({
            inviteId: String(variables.inviteId ?? ''),
            signature: variables.signature as string | undefined,
          }),
        },
      };
    }
    if (has(q, 'confirmInvitation')) {
      const partnership = variables.partnershipAgreement;
      if (partnership === false) {
        return {
          coreOrganizationInvitations: {
            confirmInvitation: rejectOrgInvite(String(variables.inviteId ?? '')),
          },
        };
      }
      return {
        coreOrganizationInvitations: {
          confirmInvitation: confirmOrgInvite({
            inviteId: String(variables.inviteId ?? ''),
            signature: variables.signature as string | undefined,
          }),
        },
      };
    }
  }
  if (has(q, 'coreOrganizationInvitations') && has(q, 'count(')) {
    const incoming = q.includes('isIncoming: true');
    return { coreOrganizationInvitations: { count: listOrgInvites(incoming).length } };
  }
  if (has(q, 'coreOrganizationInvitations')) {
    const incoming = q.includes('isIncoming: true');
    return {
      coreOrganizationInvitations: {
        query: { invites: listOrgInvites(incoming) },
      },
    };
  }

  if (has(q, 'queryBanks')) {
    return {
      coreOrganizations: {
        queryBanks: {
          organizations: [
            {
              id: 'bank-1',
              name: 'ПАО Сбербанк',
              status: 5,
              type: 'bank',
              countryCode: 'ru',
              createdAt: '2026-08-01T00:00:00.000Z',
              updatedAt: '2026-08-01T00:00:00.000Z',
            },
            {
              id: 'bank-2',
              name: 'АО Альфа-Банк',
              status: 5,
              type: 'bank',
              countryCode: 'ru',
              createdAt: '2026-08-01T00:00:00.000Z',
              updatedAt: '2026-08-01T00:00:00.000Z',
            },
          ],
        },
      },
    };
  }

  if (has(q, 'getPartners')) {
    return { coreOrganizations: { getPartners: { organizations: state.partners } } };
  }
  if (has(q, 'getPartner(') || has(q, 'getPartner ')) {
    return {
      coreOrganizations: {
        getPartner: {
          partnership_agreement_date: '2026-08-01',
          organization: partnerOrganization,
          default_payment_option: {
            id: 'pay-partner-1',
            name: 'Основной счёт',
            isDefault: true,
            accountNumber: '40702810100000000002',
            corrNumber: '30101810400000000225',
            bic: '044525225',
            currency: 'RUB',
            docId: null,
            bank: {
              id: 'bank-1',
              name: 'ПАО Сбербанк',
              status: 5,
              type: 'bank',
              countryCode: 'ru',
              createdAt: '2026-01-01T00:00:00Z',
              updatedAt: '2026-01-01T00:00:00Z',
            },
          },
        },
      },
    };
  }
  if (has(q, 'coreOrganizations') && (has(q, 'incoming') || has(q, 'isIncoming'))) {
    const incoming = q.includes('isIncoming: true') || has(q, 'incoming');
    const invites = listOrgInvites(Boolean(incoming)).map((invite) => ({
      id: invite.id,
      status: invite.status,
      type: 'organization',
      createdAt: invite.createdAt,
      updatedAt: invite.updatedAt,
      organization: incoming ? state.organization : partnerOrganization,
      partnerOrganization: incoming ? partnerOrganization : state.organization,
      joinDocumentId: invite.joinDocumentId,
    }));
    if (has(q, 'count(')) {
      return { coreOrganizations: { invitationsCount: invites.length, count: invites.length } };
    }
    return { coreOrganizations: { invitations: invites, query: { invitations: invites } } };
  }
  if (has(q, 'coreOrganizations') && q.includes('mutation')) {
    if (has(q, 'update(')) {
      return { coreOrganizations: { update: updateOrganization(inputOf(variables)) } };
    }
    return {
      coreOrganizations: {
        startInvitation: startOrgInvite({
          partnerOrganizationId: String(variables.partnerOrganizationId ?? ''),
        }).inviteId,
        finishInvitation: true,
        confirmInvitation: true,
        update: state.organization,
        // Форма ответа схемы: prepare возвращает конверт, а не саму организацию —
        // фронт читает prepare.organization.id.
        prepare: {
          isRegistered: true,
          inviteId: null,
          organization: prepareTargetOrganization(variables),
        },
      },
    };
  }
  if (has(q, 'coreOrganizations', 'get(')) {
    const known = [state.organization, partnerOrganization];
    const requested = asIds(variables);
    const found = requested.length
      ? known.filter((item) => requested.includes(item.id))
      : [];
    return { coreOrganizations: { get: found } };
  }
  if (has(q, 'coreOrganizations', 'query')) {
    return {
      coreOrganizations: { query: { organizations: [state.organization, partnerOrganization] } },
    };
  }
  if (has(q, 'coreOrganizations', 'get')) {
    return { coreOrganizations: { get: [state.organization] } };
  }

  if (has(q, 'corePOAUsers')) {
    return poaUsersResponse();
  }

  if (has(q, 'corePOA') && q.includes('mutation')) {
    const id = addPoa();
    return {
      corePOA: {
        createDraft: id,
        create: { id },
        sign: true,
        revoke: true,
        update: { id },
      },
    };
  }
  if (has(q, 'corePOA')) {
    const first = state.poas[0] as { poaDraft?: typeof poaDraft } | undefined;
    const draft = first?.poaDraft ?? poaDraft;
    return {
      corePOA: {
        query: { poas: state.poas },
        get: [draft],
        getDraft: draft,
        getSignedById: { id: draft.id, status: 'active', xml: '<xml/>' },
        getSignedRevocation: { revocation: { id: draft.id } },
        previewRevocation: {
          content: '<?xml version="1.0" encoding="UTF-8"?><Otzyv poa="МЧД-1001" />',
        },
        listRetrustPOAs: [],
      },
    };
  }

  if (has(q, 'coreDictionaries')) {
    return {
      coreDictionaries: {
        get: [
          {
            id: '74db90ed-70e7-4a03-86ae-96969b21c9af',
            alias: 'countries',
            title: 'Countries',
            enums: [
              {
                id: 'ru',
                title: 'ru',
                values: [
                  { key: 'fullname', value: 'Россия' },
                  { key: 'shortname', value: 'РФ' },
                ],
              },
            ],
          },
        ],
      },
    };
  }

  if (has(q, 'getFilterOptions')) {
    return {
      app5182dd71_OperationalRequest: {
        getFilterOptions: {
          agreementId: [
            {
              key: 'agreement-1',
              valStr: 'Договор №РД-2085-probe от 2026-08-01, Доп. соглашение №1',
            },
          ],
        },
      },
    };
  }

  if (has(q, 'sendToCorrect') && q.includes('mutation')) {
    const id = String(variables.documentId ?? variables.id ?? '');
    updateFuelItemStatus('requests', id, IDS.STATUS_REJECTED);
    const payload = { status: true, message: null };
    return {
      app5182dd71_OperationalRequest: { sendToCorrect: payload },
      app5182dd71_ShipmentTask: { sendToCorrect: payload },
      app5182dd71_ControlCard: { sendToCorrect: payload },
    };
  }

  if (has(q, 'terminate') && q.includes('mutation')) {
    const ids = asIds(variables);
    ids.forEach((id) => updateFuelItemStatus('requests', id, IDS.STATUS_CANCELLED));
    const payload = { status: true, message: null };
    return {
      app5182dd71_OperationalRequest: { terminate: payload },
      app5182dd71_ShipmentTask: { terminate: payload },
      app5182dd71_ControlCard: { terminate: payload },
    };
  }

  if (has(q, 'getList')) {
    const metaFor = (total: number) => ({ page: 1, limit: 1000, total });
    return {
      app5182dd71_OperationalRequest: {
        getList: { data: state.fuel.requests, meta: metaFor(state.fuel.requests.length) },
        getFilterOptions: {},
      },
      app5182dd71_ShipmentTask: {
        getList: { data: state.fuel.tasks, meta: metaFor(state.fuel.tasks.length) },
      },
      app5182dd71_ControlCard: {
        getList: { data: state.fuel.cards, meta: metaFor(state.fuel.cards.length) },
      },
      app5182dd71_ShipmentReport: {
        getList: { data: state.fuel.reports, meta: metaFor(state.fuel.reports.length) },
      },
      app5182dd71_ShipmentRegistry: {
        getList: { data: state.fuel.registries, meta: metaFor(state.fuel.registries.length) },
      },
    };
  }

  if (has(q, 'create(') && q.includes('mutation')) {
    const input = inputOf(variables);
    if (has(q, 'OperationalRequest')) {
      const item = addFuelItem('requests', input);
      return { app5182dd71_OperationalRequest: { create: item } };
    }
    if (has(q, 'ShipmentReport')) {
      const item = addFuelItem('reports', input);
      return { app5182dd71_ShipmentReport: { create: item } };
    }
    const created = createContract(input);
    return {
      app70e9b8a9_Document: { create: { id: created.id } },
      app5705572d_Document: { create: { id: created.id } },
      app36c5da51_Document: { create: { id: created.id } },
      app12d801b5_Document: { create: { id: created.id } },
      app1d1b2841_Document: { create: { id: created.id } },
    };
  }

  if (has(q, 'getOne') || has(q, 'getTemplate')) {
    const fuel = { template: fuelTemplate };
    const template = { template: contractTemplate };
    const requestedId = String(variables.documentId ?? variables.id ?? '');
    const operationalOne =
      state.fuel.requests.find((item) => item.id === requestedId) ?? state.fuel.requests[0];
    return {
      app5182dd71_OperationalRequest: {
        getOne: operationalOne,
        getTemplate: { template: operationalRequestTemplate },
        create: { id: 'new-1' },
      },
      app5182dd71_ShipmentTask: {
        getOne: state.fuel.tasks[0],
        getTemplate: fuel,
        update: { id: 'upd-1' },
      },
      app5182dd71_ControlCard: {
        getOne: state.fuel.cards[0],
        getTemplate: fuel,
        update: { id: 'upd-1' },
      },
      app5182dd71_ShipmentReport: {
        getOne: state.fuel.reports[0],
        getTemplate: fuel,
        create: { status: 'ok' },
      },
      app5182dd71_ShipmentRegistry: {
        getOne: state.fuel.registries[0],
        getTemplate: fuel,
      },
      app70e9b8a9_Document: { getTemplate: template, create: { id: 'doc-new' }, update: { id: 'doc-upd' } },
      app5705572d_Document: { getTemplate: template, create: { id: 'doc-new' } },
      app36c5da51_Document: { getTemplate: template, create: { id: 'doc-new' } },
      app12d801b5_Document: { getTemplate: template, create: { id: 'doc-new' } },
      app1d1b2841_Document: { getTemplate: template, create: { id: 'doc-new' } },
    };
  }

  if (q.includes('mutation')) {
    return { ok: true };
  }

  return {};
}
